import * as React from "react"
import { cn, getInitials, Avatar, AvatarFallback } from "@merels/ui"
import { MateName } from "../MateName/MateName"
import { VideoControls } from "./VideoControls/VideoControls"
import { resolveThumbnailUrl } from "../../../../assets/avatarPresets/avatarPresets"

export interface MateBoxVideoControls {
  cameraOn: boolean
  micOn: boolean
  setCameraEnabled: (enabled: boolean) => void
  setMicEnabled: (enabled: boolean) => void
}

export interface MateBoxProps {
  name: string
  /** Live camera track for this mate's device, if it's currently publishing video. */
  videoTrack?: MediaStreamTrack
  /** Live mic track for this mate's device, if it's currently publishing audio. */
  audioTrack?: MediaStreamTrack
  /**
   * True when this tile is the local mate's own device (self-view). Audio is muted
   * in that case to avoid feeding the local mic's own audio back through speakers.
   */
  isLocalParticipant?: boolean
  /** Present only for the local mate's own tile - lets them mute camera/mic. */
  videoControls?: MateBoxVideoControls
  thumbnailUrl?: string
  /** Shows the bot's active move-generation state on its own card. */
  isThinking?: boolean
  /** Present for a locally owned bot, enabling its card-local dismissal control. */
  onDismiss?: () => void
  /** The room's name for hosts, shown when this mate hosts a managed room. */
  hostBadge?: string
  /** Text of the owner's host toggle, such as "Make teacher". */
  hostToggleLabel?: string
  /** Present for the room owner, letting them make this mate a host or remove it. */
  onToggleHost?: () => void
  metaLine?: string
  noBackground?: boolean
  /** Explicit tile side length in pixels, overriding the default 112px size (e.g. for the gameless video grid). */
  sizePx?: number
  /** The tile's action: our own mate's settings dialog, or acting as an Omni mate. */
  onClick?: () => void
  /** Accessible name for the tile's action, when its name alone doesn't say what clicking does. */
  clickLabel?: string
  /** Omni mode: highlights the mate this client is acting as. */
  isActive?: boolean
  /** Present when in gameless mode for self view, toggling minimization state. */
  onToggleSelfViewMinimized?: () => void
  isSelfViewMinimized?: boolean
}

// Tracks last assigned to each <video> element's srcObject, keyed by the DOM node itself.
// The ref callback below gets a new function identity every render (it's declared inline,
// deliberately - not useCallback, per CLAUDE.md "avoid hooks"), so React re-invokes it with
// the *same* element on every re-render, not just when tracks change. Without this guard,
// srcObject would be rebuilt with a fresh MediaStream every render, visibly stuttering the tile.
const lastAssignedTracks = new WeakMap<HTMLVideoElement, MediaStreamTrack[]>()

export const MateBox = React.forwardRef<HTMLDivElement, MateBoxProps>(
  ({ name, videoTrack, audioTrack, isLocalParticipant = false, videoControls, thumbnailUrl, isThinking = false, onDismiss, hostBadge, hostToggleLabel, onToggleHost, noBackground = false, sizePx, onClick, clickLabel, isActive = false, onToggleSelfViewMinimized, isSelfViewMinimized }, ref) => {
    const hasVideo = Boolean(videoTrack)
    const hasThumbnail = !hasVideo && Boolean(thumbnailUrl)

    // Imperative srcObject assignment via ref callback (not useEffect - see CLAUDE.md
    // "avoid hooks"). Only reassigns when the actual track identities change.
    const setVideoRef = (el: HTMLVideoElement | null) => {
      if (!el) return
      const tracks = [videoTrack, audioTrack].filter((t): t is MediaStreamTrack => Boolean(t))
      const previous = lastAssignedTracks.get(el)
      const unchanged = previous && previous.length === tracks.length && previous.every((t, i) => t === tracks[i])
      if (unchanged) return
      lastAssignedTracks.set(el, tracks)
      el.srcObject = tracks.length > 0 ? new MediaStream(tracks) : null
    }

    const handleClick = onClick
      ? (e: React.MouseEvent) => {
          e.stopPropagation()
          onClick()
        }
      : undefined

    const handleKeyDown = onClick
      ? (e: React.KeyboardEvent) => {
          if (e.key !== 'Enter' && e.key !== ' ') return
          e.preventDefault()
          e.stopPropagation()
          onClick()
        }
      : undefined

    return (
      <div
        ref={ref}
        className={cn(
          "group relative overflow-hidden rounded-md aspect-square",
          !sizePx && "w-[112px]",
          noBackground ? "bg-transparent" : "bg-muted-bg",
          onClick && "cursor-pointer"
        )}
        style={sizePx ? { width: `${sizePx}px`, height: `${sizePx}px` } : undefined}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        role={onClick ? 'button' : undefined}
        tabIndex={onClick ? 0 : undefined}
        aria-label={onClick ? clickLabel : undefined}
        aria-current={isActive || undefined}
      >
        {hasVideo && (
          <video
            ref={setVideoRef}
            autoPlay
            muted={isLocalParticipant}
            playsInline
            className="absolute inset-0 w-full h-full object-cover"
          />
        )}

        {hasThumbnail && (
          <img
            src={resolveThumbnailUrl(thumbnailUrl)}
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
            draggable={false}
          />
        )}

        {!hasVideo && !hasThumbnail && (
          <Avatar className="absolute inset-0 h-full w-full rounded-none">
            <AvatarFallback className="rounded-none bg-accent/20 text-primary text-lg font-bold">
              {getInitials(name)}
            </AvatarFallback>
          </Avatar>
        )}

        {isActive && (
          // Drawn over the video/avatar, which would hide an inset ring on the tile itself.
          <div className="pointer-events-none absolute inset-0 z-10 rounded-md ring-[3px] ring-inset ring-accent" />
        )}

        <div className="absolute top-1 left-1 z-10 flex flex-col items-start gap-0.5">
          <MateName name={name} />
          {hostBadge && (
            <span className="rounded bg-accent px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-inverse">
              {hostBadge}
            </span>
          )}
        </div>

        {onToggleHost && (
          // Owner-only; revealed on hover/focus like the video controls so it
          // doesn't cover the tile at rest.
          <button
            type="button"
            className="absolute top-1 right-1 z-10 rounded bg-black/70 px-1.5 py-0.5 text-[10px] font-medium text-white opacity-0 transition-opacity hover:bg-black/85 group-hover:opacity-100 focus:opacity-100 focus:outline-none focus:ring-2 focus:ring-white"
            aria-label={`${hostToggleLabel}: ${name}`}
            onClick={(event) => {
              event.stopPropagation()
              onToggleHost()
            }}
          >
            {hostToggleLabel}
          </button>
        )}

        {onDismiss && (
          <button
            type="button"
            className="absolute top-1 right-1 z-10 rounded bg-black/70 px-1.5 py-0.5 text-xs font-medium text-white hover:bg-black/85 focus:outline-none focus:ring-2 focus:ring-white"
            aria-label={`Dismiss ${name}`}
            onClick={(event) => {
              event.stopPropagation()
              onDismiss()
            }}
          >
            ×
          </button>
        )}

        {isThinking && (
          <div className="absolute inset-x-1 bottom-1 z-10 flex items-center gap-1.5 rounded bg-black/70 px-1.5 py-1 text-[10px] font-medium text-white">
            <span className="h-2.5 w-2.5 animate-spin rounded-full border border-white/40 border-t-white" />
            Thinking…
          </div>
        )}

        {(videoControls || onToggleSelfViewMinimized) && (
          // Hidden at rest so the icons don't obscure the video; revealed on
          // hover/focus (focus-within covers keyboard/touch access to the buttons).
          // stopPropagation so camera/mic toggles don't also trigger the tile's onClick.
          <div
            className="absolute bottom-1 right-1 z-10 opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100"
            onClick={(e) => e.stopPropagation()}
          >
            <VideoControls
              cameraOn={videoControls?.cameraOn}
              micOn={videoControls?.micOn}
              onToggleCamera={videoControls ? () => videoControls.setCameraEnabled(!videoControls.cameraOn) : undefined}
              onToggleMic={videoControls ? () => videoControls.setMicEnabled(!videoControls.micOn) : undefined}
              onToggleSelfViewMinimized={onToggleSelfViewMinimized}
              isSelfViewMinimized={isSelfViewMinimized}
            />
          </div>
        )}
      </div>
    )
  }
)
MateBox.displayName = "MateBox"
